export const JSON_THEME_NAME = 'custom-json-theme';
export const JSON_THEME_NAME_DARK = 'custom-json-theme-dark';

/**
 * Monaco's active theme is a global singleton, so every editor on the page shares whichever one
 * applied last. That's why there is exactly one theme per polarity and no disabled theme: a
 * dimmed theme would win that race just as often and dim the enabled editors beside it. The
 * disabled look comes from the wrapper's `opacity-60`, which can only affect its own editor.
 */
export const getJsonThemeName = (isDarkMode: boolean) => (isDarkMode ? JSON_THEME_NAME_DARK : JSON_THEME_NAME);

// Register custom language for template variables
export const registerTemplateLanguage = (monaco: typeof import('monaco-editor')) => {
  // Register the language
  monaco.languages.register({ id: 'template' });

  // Define tokenization rules
  monaco.languages.setMonarchTokensProvider('template', {
    tokenizer: {
      root: [
        // Template variables: {{ variable_name }}
        [/\{\{\s*[^}]+\s*\}\}/, 'template-variable'],
        // Everything else is regular text
        [/[^{]+/, 'text'],
        [/\{/, 'text'], // Single braces are just text
      ],
    },
  });
};

// Define custom theme for template syntax highlighting
export const defineTemplateJsonTheme = (monaco: typeof import('monaco-editor')) => {
  monaco.editor.defineTheme(JSON_THEME_NAME, {
    base: 'vs',
    inherit: true,
    rules: [
      // Template variable styling (color only, background handled by decorations)
      { token: 'template-variable', foreground: '24585B' },
      // Regular text
      { token: 'text', foreground: '000000' },
      // JSON tokens (for when language is JSON)
      { token: 'string.key.json', foreground: '0451A5' }, // Blue for keys
      { token: 'string.value.json', foreground: 'A31515' }, // Red for string values
      { token: 'string.json', foreground: 'A31515' }, // Red for strings
      { token: 'number.json', foreground: '098658' }, // Green for numbers
      { token: 'boolean.json', foreground: '0000FF' }, // Blue for booleans
      { token: 'null.json', foreground: '0000FF' }, // Blue for null
    ],
    colors: {
      'editor.background': '#FFFFFF',
      'editor.foreground': '#374151',
      'editorCursor.foreground': '#374151',
      'editor.selectionBackground': '#E5E7EB',
      'editor.lineHighlightBackground': 'transparent',

      // Suggestion widget colors
      'editorSuggestWidget.background': '#ffffff',
      'editorSuggestWidget.foreground': '#374151',
      'editorSuggestWidget.selectedBackground': '#f3f4f6',
      'editorSuggestWidget.selectedForeground': '#1f2937',
      'editorSuggestWidget.border': '#e0e0e0',

      // List colors (affects suggestion list)
      'list.hoverBackground': '#f9fafb',
      'list.focusBackground': '#f3f4f6',
      'list.activeSelectionBackground': '#e5e7eb',
      'list.inactiveSelectionBackground': '#f3f4f6',
    },
  });

  // No `editorSuggestWidget.*` / `list.*` colors here: styling-utils already forces the whole
  // widget from `var(--*)` tokens with `!important`, so hex would be a second copy that diverges.
  monaco.editor.defineTheme(JSON_THEME_NAME_DARK, {
    base: 'vs-dark',
    inherit: true,
    rules: [
      { token: 'template-variable', foreground: 'C3B6F3' },
      { token: 'text', foreground: 'FFFFFF' },
      { token: 'string.key.json', foreground: '9CDCFE' },
      { token: 'string.value.json', foreground: 'CE9178' },
      { token: 'string.json', foreground: 'CE9178' },
      { token: 'number.json', foreground: 'B5CEA8' },
      { token: 'boolean.json', foreground: '569CD6' },
      { token: 'null.json', foreground: '569CD6' },
    ],
    colors: {
      'editor.background': '#00000000',
      'editor.foreground': '#E2E2E2',
      'editorCursor.foreground': '#FFFFFF',
      'editor.selectionBackground': '#444444',
      'editor.lineHighlightBackground': 'transparent',
    },
  });
};
